import { Portal } from '@/components/Portal';
import { cn } from '@/lib/cn';
import { arrow, autoUpdate, flip, offset, OffsetOptions, Placement, shift } from '@floating-ui/dom';
import { FloatingArrow, useFloating } from '@floating-ui/react';
import { Slot } from '@radix-ui/react-slot';
import { MarkdownBase } from 'components/markdown/MarkdownBase';
import { useTooltipContainerContext } from 'contexts/TooltipContainerContext';
import { CSSProperties, MutableRefObject, ReactNode, useCallback, useEffect, useRef, useState } from 'react';

const TOOLTIP_MARGIN = 12;
const TOOLTIP_ARROW_WIDTH = 10;
const TOOLTIP_ARROW_HEIGHT = 5;
const TOOLTIP_PLACEMENT = 'bottom';
const TOOLTIP_SHOW_DELAY = 400;
const TOOLTIP_HIDE_DELAY = 150;

// Most tooltips are never hovered, so skip the native call rather than clearing an unset timer on
// every unmount — dashboards unmount these in bulk.
const clearTimer = (timer: MutableRefObject<number | undefined>) => {
    if (timer.current !== undefined) {
        window.clearTimeout(timer.current);
        timer.current = undefined;
    }
};

export interface TooltipProps {
    title: string | ReactNode;
    placement?: Placement;
    disabled?: boolean | (() => boolean);
    delayShow?: number;
    delayHide?: number;
    offsetOptions?: OffsetOptions;
    className?: string;
    tooltipClassName?: string;
    style?: CSSProperties;
    asChild?: boolean;
    [property: string]: any;
    /** Element to use as reference for where the tooltip should be placed */
    referenceElement?: Element | null;
}

const MarkdownWrapper = ({ content }: { content: string }) => (
    <div data-theme='dark'>
        <MarkdownBase content={content} />
    </div>
);

/**
 * @param title string/ReactNode
 * @param children ReactNode
 * @param placement 'bottom' | 'top' | 'left' | 'right'
 * @param disabled boolean
 * @param variant 'dark' | 'light' | 'success' | 'warning' | 'error' | 'info'
 * @param delayShow number
 * @param delayHide number
 * @param offset number
 * @param className css that wraps your tooltip, see StatusIndicator.tsx for example
 * @param tooltipClassName css class name that wraps your tooltip popover
 */
const Tooltip: React.FC<TooltipProps> = ({
    title,
    className,
    style,
    disabled,
    children,
    tooltipClassName,
    placement = TOOLTIP_PLACEMENT,
    asChild = false,
    referenceElement,
    offsetOptions,
    ...props
}) => {
    const Comp = asChild ? Slot : 'div';
    const [isHovered, setIsHovered] = useState(false);
    const elementRef = useRef<HTMLDivElement>(null);

    const showTimer = useRef<number>();
    const hideTimer = useRef<number>();

    const handleOnMouseEnter = useCallback(() => {
        clearTimer(showTimer);
        showTimer.current = window.setTimeout(() => setIsHovered(true), TOOLTIP_SHOW_DELAY);
        clearTimer(hideTimer);
    }, []);

    const handleOnMouseLeave = useCallback(() => {
        clearTimer(hideTimer);
        hideTimer.current = window.setTimeout(() => setIsHovered(false), TOOLTIP_HIDE_DELAY);
        clearTimer(showTimer);
    }, []);

    // Dialogs move focus to their first focusable element on open, which would otherwise
    // open the tooltip unprompted. :focus-visible limits us to focus the user drove.
    const handleOnFocus = useCallback(
        (event: React.FocusEvent<HTMLElement>) => {
            if (event.target.matches(':focus-visible')) {
                handleOnMouseEnter();
            }
        },
        [handleOnMouseEnter]
    );

    // Cleanup any timeouts when the component is unmounted
    useEffect(() => {
        return () => {
            clearTimer(hideTimer);
            clearTimer(showTimer);
        };
    }, []);

    const isDisabled = typeof disabled === 'function' ? disabled() : disabled;

    return (
        <>
            <Comp
                className={className}
                style={style}
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
                onFocus={handleOnFocus}
                onBlur={handleOnMouseLeave}
                ref={elementRef}
                data-testid='tooltip'
                {...props}
            >
                {children}
            </Comp>

            {!isDisabled && isHovered && (
                <TooltipBody
                    element={referenceElement || elementRef.current!}
                    title={title}
                    placement={placement}
                    tooltipClassName={tooltipClassName}
                    handleOnMouseEnter={handleOnMouseEnter}
                    handleOnMouseLeave={handleOnMouseLeave}
                    offsetOptions={offsetOptions}
                />
            )}
        </>
    );
};

export interface TooltipBodyProps {
    element: Element;
    title: string | ReactNode;
    placement?: Placement;
    tooltipClassName?: string;
    handleOnMouseEnter?: () => void;
    handleOnMouseLeave?: () => void;
    offsetOptions?: OffsetOptions;
}

const TooltipBody: React.FC<TooltipBodyProps> = ({
    element,
    title,
    placement,
    tooltipClassName,
    handleOnMouseEnter,
    handleOnMouseLeave,
    offsetOptions
}) => {
    const arrowRef = useRef<SVGSVGElement>(null);
    const tooltipContainer = useTooltipContainerContext();

    const { x, y, strategy, refs, context } = useFloating({
        elements: { reference: element },
        placement,
        strategy: 'absolute',
        whileElementsMounted: autoUpdate,

        middleware: [
            offset(offsetOptions ?? { mainAxis: TOOLTIP_MARGIN }),
            flip({ fallbackAxisSideDirection: 'start' }),
            shift(),
            arrow({ element: arrowRef.current! })
        ]
    });

    return (
        <Portal container={tooltipContainer ?? undefined}>
            <div
                className={cn(
                    'fixed z-50 p-4 text-sm bg-tooltipBackground rounded text-textPrimary w-max max-w-xl',
                    tooltipClassName
                )}
                style={{
                    position: strategy,
                    top: y ?? 0,
                    left: x ?? 0
                }}
                ref={refs.setFloating}
                role='tooltip'
                onClick={(e) => e.stopPropagation()} // Prevent clicks on the tooltip leaking
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
            >
                <TooltipContent content={title} />
                <FloatingArrow
                    ref={arrowRef}
                    context={context}
                    className='fill-tooltipBackground'
                    width={TOOLTIP_ARROW_WIDTH}
                    height={TOOLTIP_ARROW_HEIGHT}
                />
            </div>
        </Portal>
    );
};

interface TooltipContentProps {
    content: string | ReactNode;
}

const TooltipContent: React.FC<TooltipContentProps> = ({ content }) => {
    const isRichTooltip = typeof content !== 'string';

    return (
        <div className='text-textPrimary' data-theme='dark'>
            {isRichTooltip ? <>{content}</> : <MarkdownWrapper content={content} />}
        </div>
    );
};

export default Tooltip;
